import { useMemo, type RefObject } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  useContentToken,
  useSlotGroup,
  usePropertyField,
  useGallery,
} from '@vibesites/runtime/react';
import {
  EditableToken,
  EditableSlotGroup,
} from '@vibesites/runtime/react';
import { RichTextContent } from '@vibesites/runtime/react';
import type { PhotoGallery } from '@vibesites/schema';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { useScrollReveal } from '@vibesites/theme-avenue/hooks/useScrollReveal';
import { useRevealStyles } from '@vibesites/theme-avenue/hooks/useRevealStyles';
import { GlowTrace } from './GlowTrace';

// ============================================================================
// Photo selection — pick community/exterior/neighborhood images
// ============================================================================

const NEIGHBORHOOD_KEYWORDS = [
  'exterior',
  'building',
  'community',
  'pool',
  'courtyard',
  'entrance',
  'lobby',
  'lounge',
  'outdoor',
  'patio',
  'rooftop',
  'skyline',
  'park',
  'neighborhood',
  'street',
  'aerial',
  'facade',
  'clubhouse',
  'fitness',
  'gym',
  'sunset',
  'night',
  'evening',
  'view',
  'balcony',
  'terrace',
];

function scoreNeighborhood(photo: PhotoGallery): number {
  const text = [photo.title, photo.description, photo.alt_text]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
  if (!text) return 0;
  return NEIGHBORHOOD_KEYWORDS.reduce(
    (s, kw) => s + (text.includes(kw) ? 1 : 0),
    0,
  );
}

function normalizeUrl(url: string): string {
  try {
    const u = new URL(url);
    return (u.origin + u.pathname).replace(/\/+$/, '').toLowerCase();
  } catch {
    return url
      .toLowerCase()
      .split('?')[0]
      .replace(/\/+$/, '');
  }
}

function pickPhotoIndices(
  gallery: PhotoGallery[],
  count: number,
): number[] {
  if (!gallery.length) return [];

  const seen = new Set<string>();
  const unique: { index: number; photo: PhotoGallery; score: number }[] = [];
  for (const [index, photo] of gallery.entries()) {
    if (!photo.image) continue;
    const key = normalizeUrl(photo.image);
    if (seen.has(key)) continue;
    seen.add(key);
    unique.push({ index, photo, score: scoreNeighborhood(photo) });
  }

  unique.sort((a, b) => b.score - a.score);
  const pool = unique.slice(0, Math.max(count * 2, 10));
  if (pool.length <= count) return pool.map((p) => p.index);

  const step = pool.length / count;
  const picked: number[] = [];
  for (let i = 0; i < count; i++) {
    const idx = Math.min(Math.floor(i * step), pool.length - 1);
    picked.push(pool[idx].index);
  }
  return picked;
}

// ============================================================================
// Slot group definitions
// ============================================================================

const ctaLinkDef: SlotGroupDefinition = {
  label: 'Neighborhood CTA Link',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: '/neighborhood' },
    label: {
      label: 'Link Text',
      type: 'text',
      defaultValue: 'View the Neighborhood',
    },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

function makeNeighborhoodImageDef(
  label: string,
  index: number,
): SlotGroupDefinition {
  return {
    label,
    fields: {
      image: {
        label: 'Image',
        type: 'image',
        defaultField: `photo_gallery[${index}].image`,
      },
      alt: {
        label: 'Alt Text',
        type: 'text',
        defaultField: `photo_gallery[${index}].alt_text`,
        defaultValue: 'Neighborhood',
      },
    },
    sourceBinding: {
      collection: 'photo_gallery',
      defaultIndex: index,
      fieldMappings: { image: 'image', alt: 'alt_text' },
    },
  };
}

const topLeftImageDef: SlotGroupDefinition = {
  label: 'Top Left Image',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultField: 'photo_gallery[0].image',
    },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultField: 'photo_gallery[0].alt_text',
      defaultValue: 'Neighborhood',
    },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 0,
    fieldMappings: { image: 'image', alt: 'alt_text' },
  },
};

const rightImageDef: SlotGroupDefinition = {
  label: 'Right Image',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultField: 'photo_gallery[1].image',
    },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultField: 'photo_gallery[1].alt_text',
      defaultValue: 'Neighborhood',
    },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 1,
    fieldMappings: { image: 'image', alt: 'alt_text' },
  },
};

const bottomLeftImageDef: SlotGroupDefinition = {
  label: 'Bottom Left Image',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultField: 'photo_gallery[2].image',
    },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultField: 'photo_gallery[2].alt_text',
      defaultValue: 'Neighborhood',
    },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 2,
    fieldMappings: { image: 'image', alt: 'alt_text' },
  },
};

// ============================================================================
// Component
// ============================================================================

export function NeighborhoodCallout() {
  const eyebrow = useContentToken('neighborhoodcallout.eyebrow');
  const title = useContentToken('neighborhoodcallout.title');

  const ctaLink = useSlotGroup('neighborhoodcallout.cta_link', ctaLinkDef);

  const city = usePropertyField('property_address_city') as string | null;
  const state = usePropertyField('property_address_state') as string | null;
  const propertyName = usePropertyField('property_name') as string | null;

  const gallery = useGallery();
  const [topLeftIndex, rightIndex, bottomLeftIndex] = useMemo(() => {
    const picked = pickPhotoIndices(gallery, 3);
    return [picked[0] ?? 0, picked[1] ?? 1, picked[2] ?? 2] as const;
  }, [gallery]);

  const topLeftImageDefOverride = useMemo(
    () => makeNeighborhoodImageDef('Top Left Image', topLeftIndex),
    [topLeftIndex],
  );
  const rightImageDefOverride = useMemo(
    () => makeNeighborhoodImageDef('Right Image', rightIndex),
    [rightIndex],
  );
  const bottomLeftImageDefOverride = useMemo(
    () => makeNeighborhoodImageDef('Bottom Left Image', bottomLeftIndex),
    [bottomLeftIndex],
  );

  const topLeftImage = useSlotGroup(
    'neighborhoodcallout.image_1',
    topLeftImageDefOverride,
  );
  const rightImage = useSlotGroup(
    'neighborhoodcallout.image_2',
    rightImageDefOverride,
  );
  const bottomLeftImage = useSlotGroup(
    'neighborhoodcallout.image_3',
    bottomLeftImageDefOverride,
  );

  useRevealStyles();
  const leftRef = useScrollReveal<HTMLDivElement>({ threshold: 0.2 });
  const rightRef = useScrollReveal<HTMLDivElement>({ threshold: 0.2 });

  const cityName = city || 'the area';
  const stateName = state || '';
  const locationShort = stateName ? `${cityName}, ${stateName}` : cityName;

  const defaultTitle = `Explore ${cityName} step by step.`;
  const defaultDesc = `Ideally located in ${locationShort}, the neighborhood surrounding <strong>${propertyName || 'our community'}</strong> has so much to offer.\n\nEntertainment, restaurants, schools, & services combine to create a conveniently placed home. Check out the neighborhood!`;

  return (
    <section className="py-16 md:py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          {/* Left: Photo mosaic grid */}
          <div
            ref={leftRef as RefObject<HTMLDivElement>}
            className="reveal-fade-up"
          >
            <div className="neighborhood-grid glow-trace-wrap relative rounded-sm overflow-hidden">
              <GlowTrace delay={0.5} />
              <EditableSlotGroup
                groupPath="neighborhoodcallout.image_1"
                definition={topLeftImageDefOverride}
                className="neighborhood-grid-a"
              >
                {topLeftImage.image ? (
                  <VibesitesImage
                    url={topLeftImage.image}
                    alt={topLeftImage.alt || 'Neighborhood'}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full bg-[var(--vibe-color-surface)]" />
                )}
              </EditableSlotGroup>
              <EditableSlotGroup
                groupPath="neighborhoodcallout.image_2"
                definition={rightImageDefOverride}
                className="neighborhood-grid-b"
              >
                {rightImage.image ? (
                  <VibesitesImage
                    url={rightImage.image}
                    alt={rightImage.alt || 'Neighborhood'}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full bg-[var(--vibe-color-surface)]" />
                )}
              </EditableSlotGroup>
              <EditableSlotGroup
                groupPath="neighborhoodcallout.image_3"
                definition={bottomLeftImageDefOverride}
                className="neighborhood-grid-c"
              >
                {bottomLeftImage.image ? (
                  <VibesitesImage
                    url={bottomLeftImage.image}
                    alt={bottomLeftImage.alt || 'Neighborhood'}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full bg-[var(--vibe-color-surface)]" />
                )}
              </EditableSlotGroup>
            </div>
          </div>

          {/* Right: Copy + CTA */}
          <div
            ref={rightRef as RefObject<HTMLDivElement>}
            className="reveal-fade-up"
          >
            <EditableToken
              path="neighborhoodcallout.eyebrow"
              defaultValue="Experiences Outside Your Doorstep"
              as="p"
              className="font-body text-xs sm:text-sm uppercase tracking-[0.2em] text-[var(--vibe-color-text)] mb-3"
            >
              {eyebrow}
            </EditableToken>

            <EditableToken
              path="neighborhoodcallout.title"
              defaultValue={defaultTitle}
              as="h2"
              className="font-heading text-3xl sm:text-4xl lg:text-5xl font-normal text-[var(--vibe-color-text)] mb-6 tracking-tight leading-tight"
            >
              {title || defaultTitle}
            </EditableToken>

            <RichTextContent
              path="neighborhoodcallout.description"
              label="Description"
              defaultValue={`<p>${defaultDesc.replace(/\n\n/g, '</p><p>')}</p>`}
              as="div"
              className="font-body text-sm sm:text-base text-[var(--vibe-color-text-muted)] leading-relaxed space-y-4 mb-8"
            />

            <EditableSlotGroup
              groupPath="neighborhoodcallout.cta_link"
              definition={ctaLinkDef}
            >
              <a
                href={ctaLink.url || '/neighborhood'}
                target={ctaLink._target || '_self'}
                className="neighborhood-cta-btn inline-flex items-center gap-2"
              >
                <span>
                  {ctaLink.label || 'View the Neighborhood'}
                </span>
              </a>
            </EditableSlotGroup>
          </div>
        </div>
      </div>

      <style>{`
        /* Photo mosaic — 2×2 asymmetric grid */
        .neighborhood-grid {
          display: grid;
          grid-template-columns: 1fr 1.15fr;
          grid-template-rows: 1fr 1fr;
          gap: 4px;
          max-height: 380px;
        }
        .neighborhood-grid-a {
          grid-column: 1;
          grid-row: 1;
          overflow: hidden;
        }
        .neighborhood-grid-b {
          grid-column: 2;
          grid-row: 1 / 3;
          overflow: hidden;
        }
        .neighborhood-grid-c {
          grid-column: 1;
          grid-row: 2;
          overflow: hidden;
        }
        .neighborhood-grid-d {
          display: none;
        }

        @media (min-width: 640px) {
          .neighborhood-grid {
            gap: 5px;
            max-height: 420px;
          }
        }

        @media (min-width: 1024px) {
          .neighborhood-grid {
            max-height: 440px;
          }
        }

        .neighborhood-grid img {
          transition: transform 0.6s ease;
        }
        .neighborhood-grid img:hover {
          transform: scale(1.04);
        }

        /* CTA */
        .neighborhood-cta-btn {
          padding: 0.875rem 2rem;
          font-size: 0.75rem;
          font-weight: 700;
          letter-spacing: 0.12em;
          text-transform: uppercase;
          color: var(--vibe-color-text-on-secondary);
          background: linear-gradient(
            135deg,
            var(--vibe-color-secondary) 0%,
            color-mix(in srgb, var(--vibe-color-secondary) 80%, black) 100%
          );
          border-radius: 9999px;
          transition: all 0.3s ease;
        }
        .neighborhood-cta-btn:hover {
          transform: translateY(-1px);
          box-shadow: 0 4px 16px color-mix(in srgb, var(--vibe-color-secondary) 35%, transparent);
        }
      `}</style>
    </section>
  );
}

// ============================================================================
// Content Schema
// ============================================================================

export const contentSchema: ComponentContentSchema = {
  slot: 'NeighborhoodCallout',
  displayName: 'Neighborhood Callout',
  tokens: {
    'neighborhoodcallout.eyebrow': {
      label: 'Eyebrow',
      type: 'text',
      defaultValue: 'Experiences Outside Your Doorstep',
    },
    'neighborhoodcallout.title': {
      label: 'Title',
      type: 'text',
      defaultValue: 'Explore the neighborhood.',
      suggestedFields: ['property_address_city'],
    },
    'neighborhoodcallout.description': {
      label: 'Description',
      type: 'richtext',
      defaultField: 'property_home_neighborhood_blurb',
      suggestedFields: [
        'property_home_neighborhood_blurb',
        'property_neighborhood_blurb',
      ],
    },
  },
  slotGroups: {
    'neighborhoodcallout.image_1': topLeftImageDef,
    'neighborhoodcallout.image_2': rightImageDef,
    'neighborhoodcallout.image_3': bottomLeftImageDef,
    'neighborhoodcallout.cta_link': ctaLinkDef,
  },
};

(NeighborhoodCallout as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default NeighborhoodCallout;
